import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ResumeDetails } from './types';

const initialState: ResumeDetails = {
  id: '',
  isActive: true,
  tenantId: '',
  createdDate: '',
  updatedDate: '',
  userId: '',

  title: '',

  templateId: '',
  templateVersion: 0,

  personalInfo: {
    firstName: '',
    lastName: '',
    fullName: '',
    additionalName: '',

    profilePhoto: {
      fileName: '',
      folderPath: '',
      timeStamp: '',
      bucketName: '',
      originalFileName: '',
      region: ''
    },

    dateOfBirth: '',
    placeOfBirth: '',

    email: '',
    phone: '',
    linkedinUrl: '',
    githubUrl: '',

    address: '',
    city: '',
    state: '',
    country: '',
    nationality: '',
    pincode: ''
  },

  skills: [],

  experience: [],

  education: [],

  projects: [],

  certifications: [],

  achievements: [],

  languages: [],

  preferences: {
    targetedCompany: '',
    targetedRole: '',
    preferredLanguages: '',
    areasOfInterest: ''
  },

  additionalInfo: {
    socialLinks: '',
    additionalDetails: '',
    publications: '',
    codingProfiles: ''
  },

  resumeFormState: {},
  resumeStatus: 'FORM_PENDING'
};

const resumeSlice = createSlice({
  name: 'resume',

  initialState,

  reducers: {
    /* -----------------------------
       MAIN
    ----------------------------- */

    setResume: (state, action: PayloadAction<ResumeDetails>) => {
      return {
        ...action.payload
      };
    },

    resetResume: () => initialState,

    setResumeTitle: (state, action: PayloadAction<string>) => {
      state.title = action.payload;
    },

    /* -----------------------------
       RESUME FORM STATE
    ----------------------------- */

    setResumeFormState: (state, action: PayloadAction<Record<string, any>>) => {
      state.resumeFormState = action.payload;
    },

    /* -----------------------------
       PERSONAL INFO
    ----------------------------- */

    updatePersonalInfo: (
      state,
      action: PayloadAction<Partial<ResumeDetails['personalInfo']>>
    ) => {
      state.personalInfo = {
        ...state.personalInfo,
        ...action.payload
      };
    },

    /* -----------------------------
       TEMPLATE
    ----------------------------- */

    setTemplate: (
      state,
      action: PayloadAction<{
        templateId: string;
        templateVersion: number;
      }>
    ) => {
      state.templateId = action.payload.templateId;
      state.templateVersion = action.payload.templateVersion;
    },

    /* -----------------------------
       SKILLS  (key: skillName)
    ----------------------------- */

    addSkill: (state, action: PayloadAction<ResumeDetails['skills'][0]>) => {
      state.skills = [...state.skills, action.payload];
    },

    deleteSkill: (state, action: PayloadAction<string>) => {
      state.skills = state.skills.filter(
        (skill: any) => skill.skillName !== action.payload
      );
    },

    /* -----------------------------
       EXPERIENCE  (key: jobTitle-companyName)
    ----------------------------- */

    addExperience: (
      state,
      action: PayloadAction<ResumeDetails['experience'][0]>
    ) => {
      state.experience = [
        ...state.experience,
        {
          ...action.payload,
          employmentType: action.payload.employmentType || 'FULL_TIME',
          locationType: action.payload.locationType || 'ON_SITE'
        }
      ];
    },

    deleteExperience: (state, action: PayloadAction<string>) => {
      state.experience = state.experience.filter(
        (exp: any) => `${exp.jobTitle}-${exp.companyName}` !== action.payload
      );
    },

    /* -----------------------------
       EDUCATION  (key: degree-institution)
    ----------------------------- */

    addEducation: (
      state,
      action: PayloadAction<ResumeDetails['education'][0]>
    ) => {
      state.education = [...state.education, action.payload];
    },

    deleteEducation: (state, action: PayloadAction<string>) => {
      state.education = state.education.filter(
        (edu: any) => `${edu.degree}-${edu.institution}` !== action.payload
      );
    },

    /* -----------------------------
       PROJECTS  (key: projectName)
    ----------------------------- */

    addProject: (
      state,
      action: PayloadAction<ResumeDetails['projects'][0]>
    ) => {
      state.projects = [...state.projects, action.payload];
    },

    deleteProject: (state, action: PayloadAction<string>) => {
      state.projects = state.projects.filter(
        (proj: any) => proj.projectName !== action.payload
      );
    },

    /* -----------------------------
       CERTIFICATIONS  (key: certificationName)
    ----------------------------- */

    addCertification: (
      state,
      action: PayloadAction<ResumeDetails['certifications'][0]>
    ) => {
      state.certifications = [...state.certifications, action.payload];
    },

    deleteCertification: (state, action: PayloadAction<string>) => {
      state.certifications = state.certifications.filter(
        (cert: any) => cert.name !== action.payload
      );
    },

    /* -----------------------------
       ACHIEVEMENTS  (key: title)
    ----------------------------- */

    addAchievement: (
      state,
      action: PayloadAction<ResumeDetails['achievements'][0]>
    ) => {
      state.achievements = [...state.achievements, action.payload];
    },

    deleteAchievement: (state, action: PayloadAction<string>) => {
      state.achievements = state.achievements.filter(
        (ach: any) => ach.title !== action.payload
      );
    },

    /* -----------------------------
       LANGUAGES  (key: language)
    ----------------------------- */

    addLanguage: (
      state,
      action: PayloadAction<ResumeDetails['languages'][0]>
    ) => {
      state.languages = [
        ...state.languages,
        {
          ...action.payload,
          proficiency: action.payload.proficiency || 'BASIC'
        }
      ];
    },

    deleteLanguage: (state, action: PayloadAction<string>) => {
      state.languages = state.languages.filter(
        (lang: any) => lang.name !== action.payload
      );
    },

    /* -----------------------------
       PREFERENCES
    ----------------------------- */

    updatePreferences: (
      state,
      action: PayloadAction<Partial<ResumeDetails['preferences']>>
    ) => {
      state.preferences = {
        ...state.preferences,
        ...action.payload
      };
    },

    /* -----------------------------
       ADDITIONAL INFO
    ----------------------------- */

    updateAdditionalInfo: (
      state,
      action: PayloadAction<Partial<ResumeDetails['additionalInfo']>>
    ) => {
      state.additionalInfo = {
        ...state.additionalInfo,
        ...action.payload
      };
    }
  }
});

export const {
  setResume,
  resetResume,

  setResumeTitle,

  setResumeFormState,

  updatePersonalInfo,

  setTemplate,

  addSkill,
  deleteSkill,

  addExperience,
  deleteExperience,

  addEducation,
  deleteEducation,

  addProject,
  deleteProject,

  addCertification,
  deleteCertification,

  addAchievement,
  deleteAchievement,

  addLanguage,
  deleteLanguage,

  updatePreferences,

  updateAdditionalInfo
} = resumeSlice.actions;

export default resumeSlice.reducer;
